import * as yup from "yup";
import {
  parseCommaSeparatedZipcodes,
  zipcodeTokensAreUnique,
  zipcodeTokensAreValid,
} from "util/zipcodeCommaSeparated";

export const TourSchema = yup.object().shape({
  transport_agent_id: yup.number().required("Required"),
  name: yup.string().required("Required"),
  description: yup.string().nullable(),
  color: yup
    .string()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === null || originalValue === undefined) {
        return null;
      }
      const trimmed = String(originalValue).trim();
      return trimmed === "" ? null : trimmed;
    })
    .matches(/^#([0-9a-f]{6})$/i, "Invalid color")
    .optional(),
  active: yup.bool(),
  delivery_timings: yup.array().of(yup.string().required("Required")).required("Required").min(1, "Required"),
  delivery_timing_configs: yup.array().of(
    yup.object().shape({
      delivery_timing: yup.string().required("Required"),
      scanning_codes_enabled: yup.bool(),
      allow_manual_orders_marking: yup.bool(),
      bypass_route_start_verification: yup.bool(),
      tour_type: yup
        .string()
        .oneOf(["one_way", "return_to_origin", "custom", null])
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === null || originalValue === undefined || originalValue === "") {
            return null;
          }
          return value;
        }),
      route_type: yup
        .string()
        .oneOf(["distances", "durations", "distances_google", null])
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === null || originalValue === undefined || originalValue === "") {
            return null;
          }
          return value;
        }),
      supplier_location_id: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === null) return null;
          return String(originalValue).trim() === "" ? null : value;
        })
        .typeError("Required")
        .when("custom_route_enabled", {
          is: true,
          then: (schema) => schema.required("Required"),
          otherwise: (schema) => schema.optional(),
        }),

      pickup_start: yup.number().nullable(),
      pickup_end: yup.number().nullable(),
      pickup_stops: yup
        .array()
        .of(
          yup.object().shape({
            supplier_location_id: yup.number().required("Required"),
            order: yup.number().nullable(),
          })
            .nullable()
            .transform((value, originalValue) => {
              if (originalValue === null || originalValue === undefined || originalValue === "") {
                return [];
              }
              if (typeof originalValue === "string") {
                try {
                  return JSON.parse(originalValue);
                } catch (err) {
                  return [];
                }
              }
              return value;
            }),
        ),
      end_point: yup.number().nullable(),
      fix_stops: yup
        .array()
        .of(
          yup.object().shape({
            type: yup
              .string()
              .oneOf([
                "start_point",
                "end_point",
                "before_start",
                "after_end",
                "before_end",
                "pickup_start",
                "pickup_stop",
                "pickup_end",
              ])
              .required("Type required"),
            supplier_location_id: yup.number().required("Location required"),
          })
        )
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === null || originalValue === undefined || originalValue === "") {
            return [];
          }
          if (typeof originalValue === "string") {
            try {
              return JSON.parse(originalValue);
            } catch (err) {
              return [];
            }
          }
          return value;
        }),
      stops: yup.array().of(
        yup.object().shape({
          type: yup
            .string()
            .oneOf([
              "start_point",
              "before_end",
              "pickup_start",
              "pickup_stop",
              "pickup_end",
            ])
            .required("Type required"),
          supplier_location_id: yup.number(),
        })
      )
    })
  ),
  tour_type_category: yup.string().oneOf(["delivery", "pickup", "mix"]).required("Required"),
  custom_route_enabled: yup.bool(),
  allowed_supplier_locations: yup.array().of(yup.number().required("Required")).required("Required").min(1, "Required"),
  supplier_location_id: yup
    .number()
    .nullable()
    .when(["custom_route_enabled", "tour_type_category"], {
      is: (customRouteEnabled, tourTypeCategory) => customRouteEnabled || tourTypeCategory === "pickup",
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required("Required"),
    }),
  user_defined_tour_id: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? null
        : value
    )
    .typeError("Tour ID must be a number")
    .nullable()
    .required("Required"),
  tour_type: yup
    .string()
    .oneOf(["one_way", "return_to_origin", "custom"])
    .when("tour_type_category", {
      is: (tourTypeCategory) => tourTypeCategory !== "pickup",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
  route_type: yup
    .string()
    .oneOf(["distances", "durations", "distances_google"])
    .when("tour_type_category", {
      is: (tourTypeCategory) => tourTypeCategory !== "pickup",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
  stops: yup.array().of(
    yup.object().shape({
      type: yup
        .string()
        .oneOf([
          "start_point",
          "end_point",
          "before_start",
          "after_end",
          "before_end",
          "pickup_start",
          "pickup_stop",
          "pickup_end",
        ])
        .required("Type required"),
      supplier_location_id: yup.number().required("Location required"),
    })
  ),
});

export const CustomerSchema = yup.object().shape({
  tour_mappings: yup
    .array()
    .of(
      yup.object().shape({
        tour_id: yup.number().typeError("Required").required("Required"),
        departures: yup.array().of(yup.string()).min(1, "Required").required("Required"),
      })
    )
    .min(1, "Required")
    .required("Required")
    .test("unique-departures", "Duplicate departures not allowed", (mappings) => {
      if (!Array.isArray(mappings)) return true;
      const departures = mappings.flatMap((m) => m?.departures || []);
      return departures.length === new Set(departures).size;
    }),
  name: yup.string().required("Required"),
  number: yup.string().max(10, "Less than 10 characters").required("Required"),
  street: yup.string().required("Required"),
  street_number: yup
    .string()
    .transform((v) => v?.trim())
    .required("Required"),
  city: yup.string().required("Required"),
  zipcode: yup
    .string()
    .matches(/^[A-Z0-9\s\-]{1,15}$/i, "Invalid zipcode format")
    .required("Required"),
  country: yup.string().required("Required"),
  email: yup.string().email("Invalid email").nullable(),
  phone: yup
    .string()
    .matches(/^[\d\s+\-/]*$/i, "Ungültige Nummer")
    .nullable(),
  latitude: yup.string().nullable(),
  longitude: yup.string().nullable(),
  deposit_agreement: yup.string().required("Required"),
  contact_salutation: yup.string().nullable(),
  contact_name: yup.string().nullable(),
  contact_surname: yup.string().nullable(),
  keybox_code: yup.mixed().when("deposit_agreement", ([deposit_agreement], schema) => {
    return deposit_agreement === "KEY_BOX"
      ? yup.string().required("Required")
      : yup.string().nullable();
  }),
  priority: yup.bool(),
  supplier_id: yup.number().nullable(),
  duplicate: yup.bool().nullable(),
  formatted_address: yup.string().nullable(),
  default_remarks: yup.string().nullable(),
});

export const SupplierLocationSchema = yup.object().shape({
  supplier_id: yup.string().nullable().required("Required"),
  name: yup.string().required("Required"),
  street: yup.string().required("Required"),
  street_number: yup
    .string()
    .transform((v) => v?.trim())
    .required("Required"),
  city: yup.string().required("Required"),
  zipcode: yup
    .string()
    .matches(/^[A-Z0-9\s\-]{1,15}$/i, "Invalid zipcode format")
    .required("Required"),
  country: yup.string().required("Required"),
  email: yup.string().email("Invalid email").nullable().required("Required"),
  phone: yup
    .string()
    .nullable()
    .matches(/^[\d\s+\-/]*$/i, "Ungültige Nummer")
    .required("Required"),
  latitude: yup.string().nullable(),
  longitude: yup.string().nullable(),
  formatted_address: yup.string().nullable(),
  filename: yup.mixed().nullable(),
});
export const SupplierSchema = yup.object().shape({
  name: yup.string().required("Required"),
  number: yup.string().required("Required"),
  street: yup.string().required("Required"),
  street_number: yup
    .string()
    .transform((v) => v?.trim())
    .required("Required"),
  city: yup.string().required("Required"),
  zipcode: yup
    .string()
    .matches(/^[A-Z0-9\s\-]{1,15}$/i, "Invalid zipcode format")
    .required("Required"),
  country: yup.string().required("Required"),
  email: yup.string().email("Invalid email").nullable().required("Required"),
  phone: yup
    .string()
    .nullable()
    .matches(/^[\d\s+\-/]*$/i, "Ungültige Nummer")
    .required("Required"),
  latitude: yup.string().nullable(),
  longitude: yup.string().nullable(),
  formatted_address: yup.string().nullable(),
});
export const SupplierGroupSchema = yup.object().shape({
  name: yup.string().required("Required"),
  filename: yup.mixed().nullable(),
});
export const OrderSchema = yup.object().shape({
  customer_id: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .typeError("Required")
    .required("Required"),
  description: yup.string().nullable(),
  departure_date: yup.date().typeError("Ungültiges Datum").required("Required"),
  remarks: yup.string().nullable(),
  departure: yup.string().nullable().required("Required"),
  express: yup.boolean().required(),
  order_type: yup.string().oneOf(["delivery", "return"]).required("Required"),
  delivery_notes: yup
    .array()
    .of(
      yup.object().shape({
        delivery_note_number: yup
          .string()
          .max(20, "Less than 20 characters")
          .required("Delivery note number is required"),
        packages: yup.number().min(1, "Packages must be at least 1").required("Packages is required"),
        auto: yup.boolean().required(),
      })
    )
    .min(1, "At least one delivery note is required")
    .required("Delivery notes are required"),
});

export const ReturnOrderSchema = yup.object().shape({
  customer_id: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .typeError("Required")
    .required("Required"),
  description: yup.string().nullable(),
  departure_date: yup.date().typeError("Ungültiges Datum").required("Required"),
  remarks: yup.string().nullable(),
  departure: yup.string().nullable().required("Required"),
  express: yup.boolean().required(),
  order_type: yup.string().oneOf(["delivery", "return"]).required("Required"),
  delivery_notes: yup
    .array()
    .of(
      yup.object().shape({
        delivery_note_number: yup
          .string()
          .max(20, "Less than 20 characters")
          .required("Return ID is required"),
        packages: yup.number().min(1, "Packages must be at least 1").required("Packages is required"),
        auto: yup.boolean().required(),
      })
    )
    .min(1, "At least one delivery note is required")
    .required("Delivery notes are required"),
});

export const ZipcodeSchema = yup.object().shape({
  zipcode: yup
    .string()
    .matches(/^[A-Z0-9\s\-]{1,15}$/i, "Invalid zipcode format")
    .required("Required"),
  country: yup.string().trim().length(2, "Required").required("Required"),
  tour_mappings: yup
    .array()
    .of(
      yup.object().shape({
        tour_id: yup.number().typeError("Required").required("Required"),
        departures: yup.array().of(yup.string()).min(1, "Required").required("Required"),
      })
    )
    .min(1, "Required")
    .required("Required")
    .test("unique-departures", "Duplicate departures not allowed", (mappings) => {
      if (!Array.isArray(mappings)) return true;
      const departures = mappings.flatMap((m) => m?.departures || []);
      return departures.length === new Set(departures).size;
    }),
  supplier_ids: yup
    .array()
    .of(yup.number().integer().positive())
    .min(1, "Required")
    .required("Required"),
});

export const ZipcodeCreateSchema = yup.object().shape({
  zipcodes_input: yup
    .string()
    .trim()
    .required("Required")
    .test("zipcodes-nonempty", "Required", (value) => {
      return parseCommaSeparatedZipcodes(value).length > 0;
    })
    .test("zipcodes-format", "Invalid zipcode format", (value) => {
      return zipcodeTokensAreValid(parseCommaSeparatedZipcodes(value));
    })
    .test("zipcodes-unique", "Duplicate zipcode in list", (value) => {
      return zipcodeTokensAreUnique(parseCommaSeparatedZipcodes(value));
    }),
  country: yup.string().trim().length(2, "Required").required("Required"),
  supplier_ids: yup
    .array()
    .of(yup.number().integer().positive())
    .min(1, "Required")
    .required("Required"),
  tour_mappings: yup
    .array()
    .of(
      yup.object().shape({
        tour_id: yup.number().typeError("Required").required("Required"),
        departures: yup.array().of(yup.string()).min(1, "Required").required("Required"),
      })
    )
    .min(1, "Required")
    .required("Required")
    .test("unique-departures", "Duplicate departures not allowed", (mappings) => {
      if (!Array.isArray(mappings)) return true;
      const departures = mappings.flatMap((m) => m?.departures || []);
      return departures.length === new Set(departures).size;
    }),
});
export const UserSchema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Required"),
  password: yup.string().required("Required"),
  name: yup.string().required("Required"),
  surname: yup.string().nullable(),
  supplier_id: yup.number().typeError("Must be a number").required("Required"),
  supplier_location_id: yup.number().typeError("Must be a number").required("Required"),
  role_id: yup.number().typeError("Must be a number").required("Required"),
  active: yup.boolean().default(true),
  permissions: yup.object().required("Required"),
});
export const AccountPermissionSchema = yup.object().shape({
  permissions: yup.object().shape({
    route_start_buttons: yup
      .array()
      .of(yup.string().oneOf(["mobile", "delivery_doc", ""]))
      .default([]),
    sorting_type: yup
      .string()
      .nullable()
      .when("permissions.route_start_buttons", ([buttons], schema) => {
        return buttons && buttons.includes("delivery_doc")
          ? yup.string().oneOf(["zipcode", "priority", "routing", "alphabetical"]).required("Required")
          : yup.string().nullable();
      }),
    include_delivery_doc: yup.boolean().default(false),
    scanning_codes_enabled: yup.boolean().default(false),
  }),
});
